import { DateUtil } from '../DateUtil'
import { HtmlUtil } from '../HtmlUtil'

export const CONTAINER_TYPE_RUNNING = 'running'

export const DrillMetricUnitCount = 'count'
export const DrillMetricUnitSecond = 'second'
export const DrillMetricUnitScore = 'score'
export const DrillMetricUnitFree = 'free' // drill_type里有这个值，把它当count, 自由跳绳
export const DrillMetricUnitDistance = 'millimeter' // 距离，毫米计
export const DrillMetricUnitDuration = 'duration'

export const PlayingTypeMove = 'move' // move 类型

export type DrillMetricUnit = 'count' | 'second' | 'score' | 'free' | 'millimeter'

export interface IUserInfo {
  user_no?: number
  user_id?: string
  nick_name: string
  head_img_url: string
  jump_rope_hall_of_frame?: boolean
  level_no?: number
  gender?: string
  school_info?: {
    school: string
    class_grade: number
    class_no: number
    class_grade_str?: string
  }
}

export interface IDrill {
  drill_id: string
  display_name: string
  container_type: string
  metric_unit: DrillMetricUnit
}

export interface Drill {
  name: string
  display_name: string
  container_type: string
  desc: string
  background_picture: string
  desc_time: string
  drill_type: DrillMetricUnit
  playing_type: string
  is_exam_drill: boolean // 是否体测
  finish_type: string
  metric_type: string
  finish_amount: number
  display_metric_base: number
  display_metric_unit: string
  can_challenge: boolean
  picture?: string
  tags?: string[]
}

export interface DrillUnit {
  unit: string
  label: string
}

export type DrillRecordVideoModel =
  | 'record_video_model_full'
  | 'record_video_model_only_video'
  | 'record_video_model_no_video'
  | ''

export interface IReport {
  app_version: string
  record_id: string
  record_video_model?: DrillRecordVideoModel
  score: number
  score_count: number
  success_count: number
  fail_count?: number
  duration: number
  score_duration: number
  metric_amount: number
  record_time: string
  score_grade: string
  video_url: string
  thumbed_counts: number // 点赞数
  current_user_thumbed: boolean // 当前用户是否点赞
  burned_calories: number // 用户消耗卡路里
  challenge_info: any
  detection_has_rope: string
  detection_fake_people: '' | 'Fake'
  is_deleted: number
  is_double_jump: boolean // 是否是双跳
  record_screen: boolean
  rank_authority?: string // 用户是否关闭上传视频
  apply_hall_status?: string // 名人堂申请
  event_id: string
  event_type: string
  speed_rate: number
  target_speed: number | null
  max_combo_count: number
  interrupt_count: number
  perfect_count: number
  great_count: number
  good_count: number
  nice_count: number
  miss_count: number
  single_leg_perc: number
  double_leg_perc: number
  official_grade?: string
  cheat_record?: boolean
  step_count?: number
  run_frequency?: number
  aim_frequency?: number

  best_racing: number // 最佳竞速（单位：个） 一分钟跳绳个数最多
  best_duration: number // 最佳时长（单位：毫秒） 单次drill跳绳时间最长
  best_endurance: number // 最佳耐力（单位：个） 单次drill跳绳个数最多

  combo: number
  dance_score_grade: 'B' | 'A' | 'S' | 'SS' | 'SSS'

  // 无绳申诉和结果
  no_rope_appeal_status?: {
    status: string
    treatment_summary: string
  }

  drill_info: Drill
  video_info: {
    video_url: string
    video_cover_url: string
    height: number
    width: number
  }
  user_info: IUserInfo

  room_score_info: {
    score: number
    user_info: IUserInfo
  }[]
  interactions_power: any[]
  is_limit_try: boolean

  golf_extra_info?: {
    good_count: number // 不错杆数
    normal_count: number // 正常杆数
    perfect_count: number // 完美杆数

    prepare_posture: number // 准备站姿 - 分数

    batting_posture: number // 击球姿势 - 分数
    batting_swing_height: 0 | 1 // 挥杆高度 0：标准，1：不足
    batting_rotation_range: 0 | 1 | 2 // 躯干旋转幅度 0：标准，1：不足，2：过大

    batting_strength: number // 击球强度 - 分数
    batting_swing_speed: 0 | 1 // 挥杆速度 0：标准，1：不足
  }

  has_intelligent_evaluation: boolean // 是否有智能评价
  intelligent_evaluation_status: 'generating' | 'generated' | 'abnormal' | string
}

/**
 * drill 相关可以服用的逻辑
 */
export class DrillUtil {
  public static readonly AiLabelNormal = 'normal' // 正常
  public static readonly AiLabelNormalCn = '正常'
  public static readonly AiLabelFakePeople = 'fake_people' // ai标签假人
  public static readonly AiLabelFakePeopleCn = '假人'
  public static readonly AiLabelNoRope = 'no_rope' // ai标签 无绳
  public static readonly AiLabelNoRopeCn = '无绳'
  public static readonly AiLabelDoubleJump = 'double_jump' // ai标签 双跳
  public static readonly AiLabelDoubleJumpCn = '双跳'

  public static buildAiLabelCn(aiLabel: string) {
    if (aiLabel === this.AiLabelFakePeople) {
      return this.AiLabelFakePeopleCn
    } else if (aiLabel === this.AiLabelNoRope) {
      return this.AiLabelNoRopeCn
    } else if (aiLabel === this.AiLabelDoubleJump) {
      return this.AiLabelDoubleJumpCn
    } else {
      return '' // 默认返回空，页面显示需要
    }
  }

  public static calcScore(metric_count: number, type: string) {
    if (type === DrillMetricUnitDuration) {
      return DateUtil.ms2Str(metric_count)
    }
    if (type === DrillMetricUnitDistance) {
      return metric_count / 10 // 临时按厘米计算
    }
    if (type === DrillMetricUnitSecond) {
      return this.getDurationInMinStr(metric_count)
    }
    if (type === DrillMetricUnitCount || type === DrillMetricUnitFree) {
      // https://www.notion.so/d14079a6395b435eb6980631bacc3a70
      return metric_count
    }

    return metric_count
  }

  public static calcUnit(type: string) {
    if (type === DrillMetricUnitCount || type === DrillMetricUnitCount || type === DrillMetricUnitFree) {
      return '个'
    }

    if (type === DrillMetricUnitDuration) {
      return 'cm'
    }

    return ''
  }

  public static getScore(score: number, drill: Drill): number {
    // 跑步记录的厘米
    if (drill.container_type === CONTAINER_TYPE_RUNNING) {
      return score / 100
    } else {
      return score
    }
  }

  /**
   * 把毫秒数转换成显示时间
   * 如果小于1分钟，保留一位小数，如 0:23.6
   *
   * @param durationMilliSeconds 毫秒数
   * @returns 显示的时间内容
   */
  public static getDurationInMinStr(durationMilliSeconds: number): string {
    return DateUtil.ms2Str(durationMilliSeconds)
  }

  public static isDistanceDrill(data: IReport): boolean {
    // 特殊的举例显示drill
    return data.drill_info && this.isDistanceDrillId(data.drill_info.name)
  }

  public static isDistanceDrillId(dirllId: string): boolean {
    return ['long_jump_physical_test_2103', 'sit_and_reach_physical_test_2103'].includes(dirllId)
  }

  public static getScoreShow(data: IReport): number | string {
    if (!data.drill_info) {
      return data.score
    }
    if (this.isDistanceDrill(data)) {
      return `${data.score / 10}cm`
    }
    if (DrillMetricUnitDistance === data.drill_info.drill_type) {
      return `${data.score / 10}cm` // 临时按厘米计算
    } else if (DrillMetricUnitSecond === data.drill_info.drill_type) {
      return this.getDurationInMinStr(data.score_duration)
    } else if (
      DrillMetricUnitCount === data.drill_info.drill_type ||
      DrillMetricUnitFree === data.drill_info.drill_type
    ) {
      // https://www.notion.so/d14079a6395b435eb6980631bacc3a70
      return `${data.score_count}个`
    } else if (DrillMetricUnitScore === data.drill_info.drill_type) {
      return `${data.score}分`
    } else {
      return data.score
    }
  }

  public static getHumanScore(score: number, metric_unit?: string, drill_id?: string): number | string {
    if (drill_id && DrillUtil.isDistanceDrillId(drill_id)) {
      return `${score / 10}cm`
    }
    if (DrillMetricUnitSecond === metric_unit) {
      return this.getDurationInMinStr(score)
    } else {
      return score
    }
  }

  public static buildReportFullUrl(recordId: string) {
    return HtmlUtil.buildFullLink(`/apph5/#/crazyrope/drill-report?id=${recordId}`)
  }

  public static isRestDrill(drill: { drill_id: string }) {
    return drill.drill_id.startsWith('rest')
  }

  public static isMoveType(drill: Drill) {
    // 包括广播操等
    if (drill && drill.playing_type === 'super_gcw') {
      return true // 广场舞
    }
    return !!(drill && drill.playing_type && drill.playing_type.startsWith('move'))
  }

  public static formatOrientation(orientation?: string) {
    return orientation === 'landscape' ? '横屏' : '竖屏'
  }
}
